import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import Logo from "../Assets/carren-prime-logo.png";
import { HiOutlineBars3, HiOutlineXMark } from "react-icons/hi2";
import { FiSettings, FiBarChart2, FiChevronDown, FiShield } from "react-icons/fi";
import { FaWhatsapp } from "react-icons/fa";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Collapse from "@mui/material/Collapse";
import HomeIcon from "@mui/icons-material/Home";
import FindInPageIcon from "@mui/icons-material/FindInPage";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import AppsIcon from "@mui/icons-material/Apps";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import './Navbar.css';

const products = [
  {
    title: "Lume Credit (LMS)",
    description: "End-to-end loan management platform",
    icon: <FiSettings />,
    link: "/products/lms"
  },
  {
    title: "Lume KYC",
    description: "Digital identity verification & compliance",
    icon: <FiShield />,
    link: "/products/kyc"
  },
  {
    title: "Alternative Credit Scoring",
    description: "AI-powered creditworthiness assessment",
    icon: <FiBarChart2 />,
    link: "/products/credit-scoring"
  },
  {
    title: "Automated WhatsApp",
    description: "Smart customer communication engine",
    icon: <FaWhatsapp />,
    link: "/products/whatsapp"
  }
];

const Navbar = ({ scrollToSection }) => {
  const [selectedLink, setSelectedLink] = useState("");
  const [showProducts, setShowProducts] = useState(false);
  const [openMenu, setOpenMenu] = useState(false);
  const [productsExpanded, setProductsExpanded] = useState(false);
  const [showAbout, setShowAbout] = useState(false);
  const [aboutExpanded, setAboutExpanded] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleLinkHover = (text) => {
    setSelectedLink(text);
    if (text !== "Our Solutions") {
      setShowProducts(false);
    }
    if (text !== "About Us") {
      setShowAbout(false);
    }
  };

  const handleLinkLeave = () => setSelectedLink("");

  const handleProductsHover = () => {
    setShowProducts(true);
    setSelectedLink("Our Solutions");
  };

  const handleProductsLeave = () => {
    setShowProducts(false);
    setSelectedLink("");
  };

  const handleProductsToggle = () => {
    setShowProducts(!showProducts);
    setSelectedLink(showProducts ? "" : "Our Solutions");
  };

  const handleAboutHover = () => {
    setShowAbout(true);
    setSelectedLink("About Us");
  };

  const handleAboutLeave = () => {
    setShowAbout(false);
    setSelectedLink("");
  };

  const handleDrawerClose = () => {
    setOpenMenu(false);
    setProductsExpanded(false);
    setAboutExpanded(false);
  };

  const handleProductsClick = () => {
    setProductsExpanded(!productsExpanded);
  };

  const handleAboutClick = () => {
    setAboutExpanded(!aboutExpanded);
  };

  const menuOptions = [
    { text: "Home", icon: <HomeIcon />, path: "/" },
    { text: "About Us", icon: <FindInPageIcon />, path: "/about-us" },
  ];

  return (
    <div className={`navbar-wrapper ${scrolled ? "scrolled" : ""}`}>
      <nav className="navbar-container">

        {/* Logo */}
        <div className="nav-logo-container">
          <Link to="/">
            <img src={Logo} alt="Lume Solutions Logo" />
            <span className="nav-logo-text">Lume Solutions</span>
          </Link>
        </div>

        {/* Desktop Links */}
        <div className="navbar-links-container">
          {menuOptions.map((item) => (
            item.text === "About Us" ? (
              <div
                key={item.text}
                className="about-dropdown-wrapper"
                onMouseEnter={handleAboutHover}
                onMouseLeave={handleAboutLeave}
              >
                <Link
                  to={item.path}
                  className={`navbar-menu-item ${selectedLink === item.text ? "selected" : ""}`}
                >
                  <span className="menu-item-text">
                    {item.text}
                    <FiChevronDown className={`chevron-icon ${showAbout ? 'rotated' : ''}`} />
                  </span>
                </Link>

                {showAbout && (
                  <div className="about-menu">
                    <Link to="/about-us#who-we-are" className="about-menu-item">
                      <Typography variant="body2">Who We Are</Typography>
                    </Link>
                    <Link to="/about-us#what-we-do" className="about-menu-item">
                      <Typography variant="body2">What We Do</Typography>
                    </Link>
                    <Link to="/about-us#mission-vision" className="about-menu-item">
                      <Typography variant="body2">Our Mission & Vision</Typography>
                    </Link>
                  </div>
                )}
              </div>
            ) : (
              <Link
                key={item.text}
                to={item.path}
                className={`navbar-menu-item ${selectedLink === item.text ? "selected" : ""}`}
                onMouseEnter={() => handleLinkHover(item.text)}
                onMouseLeave={handleLinkLeave}
              >
                <span className="menu-item-text">{item.text}</span>
              </Link>
            )
          ))}

          {/* Products Dropdown */}
          <div
            className="products-dropdown-wrapper"
            onMouseEnter={handleProductsHover}
            onMouseLeave={handleProductsLeave}
          >
            <div
              className={`navbar-menu-item products-link ${selectedLink === "Our Solutions" ? "selected" : ""}`}
              onClick={handleProductsToggle}
            >
              <span className="menu-item-text">
                Our Solutions
                <FiChevronDown className={`chevron-icon ${showProducts ? "rotated" : ""}`} />
              </span>
            </div>

            {showProducts && (
              <div className="products-dropdown-menu">
                {products.map((product, index) => (
                  <Link
                    key={index}
                    to={product.link}
                    className="product-dropdown-item"
                  >
                    <div className="product-dropdown-icon">
                      {product.icon}
                    </div>
                    <div className="product-dropdown-content">
                      <div className="product-dropdown-title">{product.title}</div>
                      <div className="product-dropdown-description">{product.description}</div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Blank Link */}
          <div className="navbar-menu-item" style={{ cursor: 'default' }}>
            <span className="menu-item-text"></span>
          </div>

          {/* Contact Us Button */}
          <Button
            component={Link}
            to="/book-now"
            variant="contained"
            className="navbar-contact-btn"
          >
            Contact Us
          </Button>
        </div>

        {/* Mobile Hamburger */}
        <div className="navbar-menu-container">
          <HiOutlineBars3 onClick={() => setOpenMenu(true)} />
        </div>

        {/* Mobile Drawer */}
        <Drawer open={openMenu} onClose={handleDrawerClose} anchor="right">
          <Box sx={{ width: 280 }} role="presentation">
            {/* Drawer Header */}
            <div className="drawer-header">
              <div className="drawer-logo">
                <img src={Logo} alt="Lume Solutions" />
                <span>Lume Solutions</span>
              </div>
              <HiOutlineXMark className="drawer-close-icon" onClick={handleDrawerClose} />
            </div>

            <Divider />

            <List>
              {menuOptions.filter(i => i.text !== "About Us").map((item) => (
                <ListItem key={item.text} disablePadding>
                  <ListItemButton component={Link} to={item.path} onClick={handleDrawerClose}>
                    <ListItemIcon>{item.icon}</ListItemIcon>
                    <ListItemText primary={item.text} />
                  </ListItemButton>
                </ListItem>
              ))}

              {/* About Us Expandable (mobile) */}
              <ListItem disablePadding>
                <ListItemButton onClick={handleAboutClick}>
                  <ListItemIcon>
                    <FindInPageIcon />
                  </ListItemIcon>
                  <ListItemText primary="About Us" />
                  {aboutExpanded ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
              </ListItem>

              <Collapse in={aboutExpanded} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  <ListItem disablePadding>
                    <ListItemButton
                      sx={{ pl: 4 }}
                      component={Link}
                      to="/about-us#who-we-are"
                      onClick={handleDrawerClose}
                    >
                      <ListItemText primary="Who We Are" />
                    </ListItemButton>
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemButton
                      sx={{ pl: 4 }}
                      component={Link}
                      to="/about-us#what-we-do"
                      onClick={handleDrawerClose}
                    >
                      <ListItemText primary="What We Do" />
                    </ListItemButton>
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemButton
                      sx={{ pl: 4 }}
                      component={Link}
                      to="/about-us#mission-vision"
                      onClick={handleDrawerClose}
                    >
                      <ListItemText primary="Our Mission & Vision" />
                    </ListItemButton>
                  </ListItem>
                </List>
              </Collapse>

              {/* Our Products Expandable */}
              <ListItem disablePadding>
                <ListItemButton onClick={handleProductsClick}>
                  <ListItemIcon><AppsIcon /></ListItemIcon>
                   <ListItemText primary="Our Solutions" />
                  {productsExpanded ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
              </ListItem>

              <Collapse in={productsExpanded} timeout="auto" unmountOnExit>
                <List
                  component="div"
                  disablePadding
                  sx={{
                    backgroundColor: '#065E49',
                    '& .MuiListItemIcon-root': { color: '#5BE49B' },
                    '& .MuiListItemText-primary': { color: '#ffffff' },
                    '& .MuiListItemText-secondary': { color: 'rgba(255,255,255,0.7)' }
                  }}
                >
                  {products.map((product, index) => (
                    <ListItem key={`product-${index}`} disablePadding>
                      <ListItemButton
                        sx={{ pl: 4, '&:hover': { backgroundColor: 'rgba(91,228,155,0.1)' } }}
                        component={Link}
                        to={product.link}
                        onClick={handleDrawerClose}
                      >
                        <ListItemIcon>{product.icon}</ListItemIcon>
                        <ListItemText
                          primary={product.title}
                          secondary={product.description}
                          secondaryTypographyProps={{ fontSize: '0.7rem' }}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Collapse>

              {/* Contact Us */}
              <ListItem disablePadding>
                <ListItemButton
                  component={Link}
                  to="/book-now"
                  onClick={handleDrawerClose}
                  sx={{
                    mx: 2,
                    my: 1.4,
                    borderRadius: '20px',
                    backgroundColor: '#118D57',
                    '&:hover': { backgroundColor: '#059669' }
                  }}
                >
                  <ListItemIcon sx={{ color: '#ffffff !important' }}><CalendarMonthIcon /></ListItemIcon>
                  <ListItemText
                    primary="Contact Us"
                    sx={{ '& .MuiListItemText-primary': { color: '#ffffff', fontWeight: 600 } }}
                  />
                </ListItemButton>
              </ListItem>
            </List>
          </Box>
        </Drawer>
      </nav>
    </div>
  );
};

export default Navbar;
